// Streams a `log` subresource (backlog first, then live with follow=true, ADR-020) into parsed lines for the
// LogViewer. Updates are batched per animation frame so a fast stream never re-renders once per line.
import { NetworkError, subresourcePath, useApi } from "@nodus/api";
import { parseLogLine, splitChunk, type LogLine } from "@nodus/ui";
import * as React from "react";

export interface LogOptions {
  follow: boolean;
  rank?: number | "all";
  index?: number;
  attempt?: string;
  process?: string;
  /** Reopen the latest-worker stream when its attempt or execution phase changes. Not an API parameter. */
  sourceVersion?: string;
}

export function useLogLines(kind: string, name: string, scope: { org: string; project: string }, opts: LogOptions) {
  const { client } = useApi();
  const [lines, setLines] = React.useState<LogLine[]>([]);
  const [live, setLive] = React.useState(opts.follow);
  const [error, setError] = React.useState<unknown>();
  const [isLoading, setLoading] = React.useState(true);
  const [reconnecting, setReconnecting] = React.useState(false);
  const [revision, setRevision] = React.useState(0);
  const retry = React.useCallback(() => setRevision((value) => value + 1), []);
  React.useEffect(() => {
    const abort = new AbortController();
    let frame = 0;
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    setLines([]);
    setError(undefined);
    setLoading(true);
    setReconnecting(false);
    setLive(opts.follow);
    void (async () => {
      // Log responses replay their backlog and have no public resume cursor. Each reconnect replaces that
      // snapshot on its first complete line instead of appending duplicates or guessing which lines to drop.
      for (let reconnects = 0; !abort.signal.aborted; reconnects++) {
        let buffer: LogLine[] = [];
        let n = 0;
        let replace = true;
        const flush = () => {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
          if (abort.signal.aborted || !buffer.length) return;
          const batch = buffer;
          const replacing = replace;
          buffer = [];
          replace = false;
          setLines((prev) => replacing ? batch : prev.concat(batch));
        };
        try {
          const res = await client.send("GET", subresourcePath(kind, name, "log", scope.project), {
            org: scope.org,
            accept: "text/plain",
            query: { follow: opts.follow, timestamps: true, rank: opts.rank, index: opts.index, attempt: opts.attempt, process: opts.process },
            signal: abort.signal,
          });
          if (abort.signal.aborted) return;
          setReconnecting(false);
          setLive(opts.follow);
          // A finite backlog is not ready to download until its last chunk has arrived.
          if (opts.follow) setLoading(false);
          reader = res.body?.getReader();
          const decoder = new TextDecoder();
          let pending = "";
          while (reader) {
            let chunk: ReadableStreamReadResult<Uint8Array>;
            try {
              chunk = await reader.read();
            } catch (e) {
              // Body transport failures occur after client.send has returned its successful response.
              throw new NetworkError("Log stream interrupted", e);
            }
            if (abort.signal.aborted) return;
            if (chunk.done) break;
            const split = splitChunk(pending, decoder.decode(chunk.value, { stream: true }));
            pending = split.pending;
            // The text log carries no stream tag; never infer stderr from customer content.
            for (const raw of split.lines) buffer.push(parseLogLine(raw, ++n));
            if (!frame) frame = requestAnimationFrame(flush);
          }
          pending += decoder.decode();
          if (pending) buffer.push(parseLogLine(pending, ++n));
          flush();
          if (!n) setLines([]);
          break;
        } catch (e) {
          if (abort.signal.aborted) return;
          flush();
          if (!opts.follow || !(e instanceof NetworkError) || reconnects >= 3) {
            setError(e);
            break;
          }
          setLive(false);
          setLoading(true);
          setReconnecting(true);
          await new Promise<void>((resolve) => {
            const done = () => {
              clearTimeout(timer);
              abort.signal.removeEventListener("abort", done);
              resolve();
            };
            const timer = setTimeout(done, 1000 * 2 ** reconnects);
            abort.signal.addEventListener("abort", done, { once: true });
          });
        } finally {
          reader?.releaseLock();
          reader = undefined;
        }
      }
      if (!abort.signal.aborted) {
        setLive(false);
        setLoading(false);
        setReconnecting(false);
      }
    })();
    return () => {
      abort.abort();
      void reader?.cancel().catch(() => {});
      if (frame) cancelAnimationFrame(frame);
    };
  }, [client, kind, name, scope.org, scope.project, opts.follow, opts.rank, opts.index, opts.attempt, opts.process, opts.sourceVersion, revision]);
  const download = React.useCallback(() => {
    const text = lines.map((line) => [
      line.timestamp,
      line.rank === undefined ? undefined : `[r${line.rank}]`,
      line.index === undefined ? undefined : `[i${line.index}]`,
      line.text,
    ].filter((value) => value !== undefined).join(" ")).join("\n");
    const url = URL.createObjectURL(new Blob([text + (lines.length ? "\n" : "")], { type: "text/plain;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${name}-logs.txt`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [lines, name]);
  return { lines, live, error, isLoading, reconnecting, retry, download };
}
